import React, { useState, useEffect } from 'react';
import { createStyles, makeStyles, Tooltip } from '@material-ui/core';
import { isMsgOut } from '../../../utils';
import ChatAPI, { MessageStatus as MS, MessageType, Message as MessageModel, MediaMessage } from '../../../chat-api';
import MessageFooter from "./MessageFooter/MessageFooter";
import MessageBodyVideo from './MessageBody/MessageBodyVideo/MessageBodyVideo';
import MessageBodyImage from './MessageBody/MessageBodyImage/MessageBodyImage';
import MessageBodyDocument from './MessageBody/MessageBodyDocument/MessageBodyDocument';
import MessageBodyAudio from './MessageBody/MessaBodyAudio/MessageBodyAudio';
import MessageBodyLocation from './MessageBody/MessageBodyLocation/MessageBodyLocation'
import {
  ContainerLoadingMedia,
  RowLoadingMedia,
  LoadingIconDiv,
  ContainerLoadingMediaBefore,
  MessageStatusError,
  MessageErrorIcon,
  TextContainer,
  MessageBody,
  MessageContainer,
  ContainerLittleLoadingMedia,
  RowLittleLoadingMedia,
  LittleLoadingIconDiv,
  ContainerLittleLoadingMediaBefore,
  StyledAvatar,
  DotTyping,
  DotContainer
} from './styles';
import { useAppState, useChatState, useTheme } from '../../../data';
import { CSSProperties } from '@material-ui/styles';

export interface MessageProps {
  message: any,
  index: number,
  lastChild?: Boolean,
  animate?: Boolean
}

export const isMedia = (type: MessageType) => {
  return ([MessageType.Audio, MessageType.Document, MessageType.Image, MessageType.Sticker, MessageType.Video].includes(type));
}

export const hasCaption = (message: MessageModel) => {
  if (message.type === MessageType.Document)
    return true;
  return !!(message as MediaMessage).body.caption;
}

export const hasTextOrCaption = (message: MessageModel) => {
  if (message.type === MessageType.Document)
    return true;
  if (isMedia(message.type)) {
    return hasCaption(message);
  }
  return true;
}

const useTooltip = makeStyles(() => createStyles({
  tooltip: {
    order: 3,
  }
}));

const Message = ({ message, index, lastChild, animate }: MessageProps) => {
  const { backgroundColor, customer, enterprise } = useTheme().body!;
  const { backgroundColor: headerBackgorunColor, titleColor } = useTheme().header!;
  const chatAPI = new ChatAPI();
  const { showAttendant, communicationTool } = useAppState();
  const { chat, avatarURL } = useChatState();
  const [mediaImage, setMediaImage] = useState<HTMLImageElement | null>(null);
  const [mediaVideo, setMediaVideo] = useState<HTMLVideoElement | null>(null);
  const [mediaDocument, setMediaDocument] = useState<HTMLAnchorElement | null>(null);
  const [mediaAudio, setMediaAudio] = useState<HTMLAudioElement | null>(null);
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const tooltipClass = useTooltip();
  const [attendant, setAttendant] = useState<string>(null!);

  useEffect(() => {
    if (showAttendant && message.attendant && message.attendant.photo) {
      chatAPI.downloadPhoto(message.attendant.photo).then(file => {
        setAttendant(file.url);
      })
    }
  }, [chatAPI, message, showAttendant]);

  useEffect(() => {
    const mediaMessage = (message as MediaMessage);
    if (mediaMessage.body?.type === MessageType.Image) {
      chatAPI.getFile(mediaMessage.body?.file).then(file => {
        const img = new Image();
        img.src = file.url;
        setImageBlob(file.blob);
        img.onload = () => setMediaImage(img);
      });
    } else if (mediaMessage.body?.type === MessageType.Video) {
      chatAPI.getFile(mediaMessage.body?.file).then(file => {
        const video = document.createElement('video');
        video.src = file.url;
        video.onloadeddata = () => setMediaVideo(video);
      });
    } else if (mediaMessage.body?.type === MessageType.Document) {
      chatAPI.getFile(mediaMessage.body?.file).then(file => {
        const a = document.createElement('a');
        a.href = file.downloadLink!;
        setMediaDocument(a);
        setMediaBlob(file?.blob);
      })
    } else if (mediaMessage.body?.type === MessageType.Audio) {
      chatAPI.getFile(message.body?.file).then(file => {
        const audio = new Audio(file.url);
        audio.src = file.url;
        audio.onloadeddata = () => setMediaAudio(audio);
      });
    }
  }, [message, chatAPI]);

  const isOutMessage = isMsgOut(message, chat?.communicationTool || communicationTool);
  let style = {};

  if (!animate)
    style = {
      animation: 'none'
    } as CSSProperties;

  return (
    <MessageContainer as="sdiv"
      style={{
        ...(message.type === MessageType.VideoConference ? { alignItems: 'center' } : isOutMessage ? { alignItems: 'flex-start' } : { marginLeft: '45px' }),
        ...(lastChild ? { marginBottom: '12px' } : { marginBottom: '5px' })
      }}
    >
      {!message.loading ?
        <MessageBody as="sdiv" style={style}>
          {lastChild ?
            (isOutMessage && message.type !== MessageType.VideoConference) &&
            ((showAttendant && message.attendant) ?
              <StyledAvatar src={attendant} textColor={titleColor} backgroundColor={headerBackgorunColor} alt={chat?.communicationTool?.user?.name}>
                {message.attendant.name.charAt(0)}
              </StyledAvatar>
              :
              <StyledAvatar src={avatarURL} textColor={titleColor} backgroundColor={headerBackgorunColor} alt={chat?.communicationTool?.user?.name}>
                {chat ? chat.communicationTool?.user?.name.charAt(0) : message.from.user?.name.charAt(0)}
              </StyledAvatar>)
            :
            (isOutMessage && message.type !== MessageType.VideoConference) &&
            <div style={{ marginRight: 45 }}></div>
          }

          {/* Error icon when the message fails */}
          {(isOutMessage && message.status === MS.Failed) &&
            <Tooltip className={tooltipClass.tooltip} placement="top-start" arrow title={`Erro ao enviar mensage! Código do erro: ${message.error}`}>
              <MessageStatusError as="sdiv">
                <MessageErrorIcon as="sdiv" role="button">
                  <span data-icon="error">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22" width="22" height="22">
                      <path fill="currentColor" d="M12.008 16.52H9.992v-2.033h2.016v2.033zm0-4.472H9.992V5.48h2.016v6.568z">
                      </path>
                    </svg>
                  </span>
                </MessageErrorIcon>
              </MessageStatusError>
            </Tooltip>
          }
          <TextContainer as="sdiv"
            isOut={!!isOutMessage && message.type !== MessageType.VideoConference}
            style={{
              ...(message.type === MessageType.VideoConference ? { backgroundColor: 'transparent', boxShadow: 'unset' } : isOutMessage ? { backgroundColor: enterprise?.messageBackgroundColor! } : { backgroundColor: customer?.messageBackgroundColor! })
            }}
          >
            {message.type === MessageType.Image &&
              <MessageBodyImage message={message} mediaImage={mediaImage} imageBlob={imageBlob} />
            }
            {message.type === MessageType.Video &&
              <MessageBodyVideo message={message} mediaVideo={mediaVideo} />
            }
            {message.type === MessageType.Audio &&
              <MessageBodyAudio
                message={message}
                mediaAudio={mediaAudio}
                runnableTrackColor={isOutMessage ? enterprise?.runnableTrackColor : customer?.runnableTrackColor}
                sliderTrackColor={isOutMessage ? enterprise?.sliderTrackColor : customer?.sliderTrackColor}
                backgroundColor={isOutMessage ? enterprise?.messageBackgroundColor : customer?.messageBackgroundColor}
                textColor={isOutMessage ? enterprise?.messageTextColor : customer?.messageTextColor}
              />
            }
            {message.type === MessageType.Document &&
              <MessageBodyDocument
                message={message}
                mediaDocument={mediaDocument}
                mediaBlob={mediaBlob}
                chat={chat}
                backgroundColor={backgroundColor}
                textColor={isOutMessage ? enterprise?.messageTextColor : customer?.messageTextColor}
              />
            }
            {message.type === MessageType.Location &&
              <MessageBodyLocation
                textColor={isOutMessage ? enterprise?.messageTextColor : customer?.messageTextColor}
                message={message}
              />
            }
            {message.type === MessageType.Sticker &&
              <sdiv style={{ padding: '5px 20px 0px 10px' }}>
                Mensagem de <b>sticker</b> não suportada
            </sdiv>
            }
            <MessageFooter
              message={message}
              chat={chat}
              hourTextColor={isOutMessage ? enterprise?.messageHourTextColor : customer?.messageHourTextColor}
              textColor={isOutMessage ? enterprise?.messageTextColor : customer?.messageTextColor}
            />
          </TextContainer>
        </MessageBody>
        :
        <MessageBody as="sdiv">
            {(isOutMessage && message.type !== MessageType.VideoConference) &&
              ((message.attendant) ?
                <StyledAvatar src={attendant} textColor={titleColor} backgroundColor={headerBackgorunColor} alt={chat?.communicationTool?.user?.name}>
                  {message.attendant.name.charAt(0)}
                </StyledAvatar>
                :
                <StyledAvatar src={avatarURL} textColor={titleColor} backgroundColor={headerBackgorunColor} alt={chat?.communicationTool?.user?.name}>
                  {chat ? chat.communicationTool?.user?.name.charAt(0) : message.from.user?.name.charAt(0)}
                </StyledAvatar>)
            }

          <DotContainer>
            <DotTyping />
          </DotContainer>
        </MessageBody>
      }
    </MessageContainer>
  );
};

export const LittleLoading = React.memo(({ message, textColor }: { message: MediaMessage, textColor?: string }) => {
  return (
    <ContainerLittleLoadingMedia as="sdiv" onClick={() => message.cancelToken?.cancel()}>
      <RowLittleLoadingMedia as="sdiv">
        <LittleLoadingIconDiv as="sdiv">
          <svg className={'wppMediaLoading'} width="38" height="38" viewBox="0 0 43 43">
            <circle className={'wppMediaLoadingCircle'} style={{ stroke: textColor }} cx="21.5" cy="21.5" r="15" fill="none" strokeWidth="3"></circle>
          </svg>
        </LittleLoadingIconDiv>
        <ContainerLittleLoadingMediaBefore as="sdiv">
          <sdiv data-icon="media-cancel" style={{ height: '34px', color: textColor }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44" width="34" height="34">
              <path fill="currentColor" d="M29.377 16.099l-1.475-1.475L22 20.525l-5.901-5.901-1.476 1.475L20.525 22l-5.901 5.901 1.476 1.475 5.9-5.901 5.901 5.901 1.475-1.475L23.475 22l5.902-5.901z"></path>
            </svg>
          </sdiv>
        </ContainerLittleLoadingMediaBefore>
      </RowLittleLoadingMedia>
    </ContainerLittleLoadingMedia>
  )
}, (prevProps, nextProps) => {
  return prevProps.message === nextProps.message
});

export const Loading = React.memo(({ message }: { message: MediaMessage }) => {
  return (
    <ContainerLoadingMedia as="sdiv" onClick={() => message.cancelToken?.cancel()}>
      <RowLoadingMedia as="sdiv" onClick={() => message.cancelToken?.cancel()}>
        <LoadingIconDiv as="sdiv" onClick={() => message.cancelToken?.cancel()}>
          <svg className={'wppMediaLoading'} width="50" height="50" viewBox="0 0 43 43">
            <circle className={'wppMediaLoadingCircle'} cx="21.5" cy="21.5" r="20" fill="none" strokeWidth="3"></circle>
          </svg>
        </LoadingIconDiv>
        <ContainerLoadingMediaBefore as="sdiv" onClick={() => message.cancelToken?.cancel()}>
          <span data-icon="media-cancel">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44" width="44" height="44">
              <path fill="currentColor" d="M29.377 16.099l-1.475-1.475L22 20.525l-5.901-5.901-1.476 1.475L20.525 22l-5.901 5.901 1.476 1.475 5.9-5.901 5.901 5.901 1.475-1.475L23.475 22l5.902-5.901z"></path>
            </svg>
          </span>
        </ContainerLoadingMediaBefore>
      </RowLoadingMedia>
    </ContainerLoadingMedia>
  )
}, (prevProps, nextProps) => {
  return prevProps.message === nextProps.message
});

export default React.memo(Message, (prevProps, nextProps) => {
  return prevProps.message === nextProps.message &&
    prevProps.message.status === nextProps.message.status &&
    prevProps.lastChild === nextProps.lastChild &&
    prevProps.message.loading === nextProps.message.loading;
});
